import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useAnchorPosition, } from './useAnchorPosition';
import { useCurrent } from './useCurrent';
import { useIsomorphicLayoutEffect as useLayoutEffect } from './useIsomorphicLayoutEffect';
import { queueMicrotask } from './queueMicrotask';
export function useFloatingElement(options = {}) {
    const { placement, open, watchStyleChanges } = options;
    const anchorRef = useRef(null);
    const floatingRef = useRef(null);
    const optionsRef = useCurrent(options);
    const [anchorPoint, setAnchorPoint] = useState(placement);
    const [CSSProperties, setCSSProperties] = useState({
        position: 'absolute',
        left: 0,
        top: 0,
    });
    const [anchor, setAnchor] = useState(null);
    const [floating, setFloating] = useState(null);
    const getAnchorPosition = useAnchorPosition(anchorRef, floatingRef);
    const [isPositioned, setIsPositioned] = useState(false);
    const currentCSSProperties = useCurrent(CSSProperties);
    const currentIsPositioned = useCurrent(isPositioned);
    const setElementStyles = useCallback(() => {
        queueMicrotask(() => {
            const anchorPosition = getAnchorPosition(optionsRef.current);
            if (!anchorPosition)
                return;
            const { position: { left, top }, strategy = 'absolute', styles, point, } = anchorPosition;
            const nextCssProperties = {
                ...currentCSSProperties.current,
                ...styles,
                position: strategy,
                transform: `translate3d(${left}px, ${top}px, 0)`,
            };
            flushSync(() => {
                if (!currentIsPositioned.current) {
                    setIsPositioned(true);
                }
                if (!deepEqual(currentCSSProperties.current, nextCssProperties)) {
                    flushSync(() => {
                        setAnchorPoint(point);
                        setCSSProperties(nextCssProperties);
                    });
                }
            });
        });
    }, [
        currentCSSProperties,
        currentIsPositioned,
        getAnchorPosition,
        optionsRef,
    ]);
    const resizeObserver = useRef(null);
    const mutationObserver = useRef(null);
    useEffect(() => {
        if (!anchor || !floating) {
            return;
        }
        let initialResizeUpdate = true;
        mutationObserver.current = new MutationObserver(() => {
            window.requestAnimationFrame(() => {
                setElementStyles();
            });
        });
        resizeObserver.current = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                if (!initialResizeUpdate) {
                    setElementStyles();
                }
                initialResizeUpdate = false;
            });
        });
        const scrollUpdate = () => {
            if (currentCSSProperties.current.position !== 'fixed') {
                setElementStyles();
            }
        };
        window === null || window === void 0 ? void 0 : window.addEventListener('scroll', scrollUpdate, {
            passive: true,
            capture: true,
        });
        window === null || window === void 0 ? void 0 : window.addEventListener('resize', setElementStyles);
        if (watchStyleChanges) {
            mutationObserver.current.observe(anchor, {
                attributeFilter: ['style'], // Only the "style" attribute
                attributeOldValue: true, // Report also the oldValue
            });
        }
        resizeObserver.current.observe(anchor);
        resizeObserver.current.observe(floating);
        return () => {
            window.removeEventListener('scroll', scrollUpdate, {
                capture: true,
            });
            window.removeEventListener('resize', setElementStyles);
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
                resizeObserver.current = null;
            }
            if (mutationObserver.current) {
                mutationObserver.current.disconnect();
                mutationObserver.current = null;
            }
        };
    }, [
        anchor,
        currentCSSProperties,
        floating,
        setElementStyles,
        watchStyleChanges,
    ]);
    useLayoutEffect(() => {
        if (open === false && currentIsPositioned.current) {
            setIsPositioned(false);
        }
    }, [currentIsPositioned, open]);
    useLayoutEffect(() => {
        if (anchor && floating) {
            setElementStyles();
        }
    }, [anchor, floating, placement, setElementStyles]);
    const refs = useMemo(() => ({
        anchor: anchorRef,
        floating: floatingRef,
        setAnchor(element) {
            setAnchor(element);
            anchorRef.current = element;
        },
        setFloating(element) {
            setFloating(element);
            floatingRef.current = element;
        },
    }), []);
    return useMemo(() => ({
        refs,
        anchor: refs.setAnchor,
        floating: refs.setFloating,
        floatingStyles: CSSProperties,
        anchorPoint,
        isPositioned,
    }), [CSSProperties, anchorPoint, isPositioned, refs]);
}
// Fork of `fast-deep-equal` that only does the comparisons we need
export function deepEqual(a, b) {
    if (a === b) {
        return true;
    }
    if (typeof a !== typeof b) {
        return false;
    }
    let length, i, keys;
    if (a && b && typeof a == 'object') {
        if (Array.isArray(a)) {
            length = a.length;
            // eslint-disable-next-line eqeqeq
            if (length != b.length)
                return false;
            for (i = length; i-- !== 0;) {
                if (!deepEqual(a[i], b[i])) {
                    return false;
                }
            }
            return true;
        }
        keys = Object.keys(a);
        length = keys.length;
        if (length !== Object.keys(b).length) {
            return false;
        }
        for (i = length; i-- !== 0;) {
            if (!{}.hasOwnProperty.call(b, keys[i])) {
                return false;
            }
        }
        for (i = length; i-- !== 0;) {
            const key = keys[i];
            if (key === '_owner' && a.$$typeof) {
                continue;
            }
            if (!deepEqual(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }
    // eslint-disable-next-line no-self-compare
    return a !== a && b !== b;
}
//# sourceMappingURL=useFloatingElement.js.map